// Smooth preset transition engine
// All transitions: ramp threshold up → snap dither params at peak → ramp down.
// Scene-affecting params (exposure, colors, reflections) always lerp smoothly.
const DURATION = 1.0 // seconds
const PEAK_THRESHOLD = 1.3 // high enough to fully kill the noise mask

let active = false
let startTime = 0
let fromSnap = {}
let toSnap = {}
let onComplete = null
let snappedAtPeak = false

// --- Color utilities ---
const hexToRgb = (hex) => {
    const n = parseInt(hex.slice(1), 16)
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

const rgbToHex = (r, g, b) =>
    '#' + ((1 << 24) | (Math.round(r) << 16) | (Math.round(g) << 8) | Math.round(b)).toString(16).slice(1)

const lerpColor = (a, b, t) => {
    const [ar, ag, ab] = hexToRgb(a)
    const [br, bg, bb] = hexToRgb(b)
    return rgbToHex(ar + (br - ar) * t, ag + (bg - ag) * t, ab + (bb - ab) * t)
}

const isColor = (v) => typeof v === 'string' && v.startsWith('#')
const INTEGERS = new Set(['pixelSize', 'mode', 'toonLevels'])
// Params that snap instantly
const SNAP_KEYS = new Set(['lightformerSpeed', 'noiseSpeed'])
// Params that affect the base 3D render — always lerp smoothly
const SCENE_KEYS = new Set([
    'backgroundColor', 'exposure', 'envMapIntensity',
    'autoRotateSpeed',
    'toonColor1', 'toonColor2', 'toonColor3', 'toonColor4',
])

const ease = (t) => 1 - Math.pow(1 - t, 3)

const lerpValue = (key, from, to, t) => {
    if (isColor(from) && isColor(to)) return lerpColor(from, to, t)
    if (typeof from === 'boolean') return t >= 0.5 ? to : from
    if (typeof from === 'number') {
        const v = from + (to - from) * t
        return INTEGERS.has(key) ? Math.round(v) : v
    }
    return t >= 0.5 ? to : from
}

export const startTransition = (currentParams, targetParams, callback) => {
    fromSnap = {}
    toSnap = {}
    for (const key in targetParams) {
        fromSnap[key] = currentParams[key] !== undefined ? currentParams[key] : targetParams[key]
        toSnap[key] = targetParams[key]
    }
    snappedAtPeak = false
    startTime = performance.now() / 1000
    active = true
    onComplete = callback
}

export const isTransitioning = () => active

export const updateTransition = (params) => {
    if (!active) return false

    const elapsed = performance.now() / 1000 - startTime
    const rawT = Math.min(elapsed / DURATION, 1)
    const t = ease(rawT)

    // --- noiseThreshold: up → peak → down ---
    const fromTh = fromSnap.noiseThreshold
    const toTh = toSnap.noiseThreshold
    if (rawT < 0.5) {
        params.noiseThreshold = fromTh + (PEAK_THRESHOLD - fromTh) * ease(rawT * 2)
    } else {
        // Snap dither-only params at peak (once)
        if (!snappedAtPeak) {
            for (const key in toSnap) {
                if (key === 'noiseThreshold' || SCENE_KEYS.has(key)) continue
                params[key] = toSnap[key]
            }
            snappedAtPeak = true
        }
        params.noiseThreshold = PEAK_THRESHOLD + (toTh - PEAK_THRESHOLD) * ease((rawT - 0.5) * 2)
    }

    // --- Scene-affecting params: always smooth lerp ---
    for (const key of SCENE_KEYS) {
        const from = fromSnap[key]
        const to = toSnap[key]
        if (from === to || from === undefined) continue
        params[key] = lerpValue(key, from, to, t)
    }

    if (rawT >= 1) {
        active = false
        for (const key in toSnap) params[key] = toSnap[key]
        if (onComplete) onComplete()
    }

    return true
}
